import { NonconformityStatus, type CreateMeetingInput } from '@qualicloud/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { MeetingType } from '@/lib/api';
import { PeoplePicker } from '@/components/sgq/people-picker';
import {
  EmptyState,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Combobox,
  Field,
  FormError,
  IconAlertTriangle,
  IconClose,
  Input,
  Select,
  Textarea,
  useToast,
} from '@/design-system';
import {
  meetingTypesApi,
  meetingsApi,
  nonconformitiesApi,
  queryKeys,
  toUserMessage,
} from '@/lib/api';
import { useDirectory } from '@/lib/use-directory';
import type { MeetingSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';
import { CadastroRapidoDeTipoDeReuniao } from '@/components/cadastro-rapido';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AGENDAMENTO DE REUNIÃO — sgq.md §B.2
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * DUAS REGRAS QUE O LEGADO NÃO TINHA
 *  · uma SACAP EXIGE ao menos uma NC vinculada. Sem vínculo não há o que
 *    analisar, e `assertMeetingCanBeClosed` recusaria o encerramento depois —
 *    barrar aqui evita a reunião nascer inútil;
 *  · o convite é NOTIFICAÇÃO enfileirada. No legado a reunião só era gravada se
 *    houvesse ao menos um e-mail resolvido: um participante sem e-mail
 *    cadastrado fazia a reunião inteira não ser gravada, sem mensagem clara.
 *
 * `startsAt`/`endsAt` são `TIMESTAMPTZ`. O legado convertia `dd/mm/aaaa hh:mm`
 * com `substr` (BUG-REU-13); aqui o campo é `datetime-local` e o valor viaja
 * como instante.
 */
/**
 * Situações em que a NC tem o que ser analisado numa SACAP: já foi considerada
 * procedente e ainda não foi encerrada. Aberta, em avaliação, improcedente e
 * encerrada ficam de fora.
 */
const TREATABLE_STATUSES: NonconformityStatus[] = [
  NonconformityStatus.ACCEPTED,
  NonconformityStatus.IN_TREATMENT,
  NonconformityStatus.PENDING_EFFECTIVENESS,
];

export function MeetingFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const directory = useDirectory();
  const search = useSearch({ strict: false }) as unknown as MeetingSearch;

  const [subject, setSubject] = useState('');
  const [meetingTypeId, setMeetingTypeId] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [agenda, setAgenda] = useState('');
  const [participantUserIds, setParticipantUserIds] = useState<string[]>([]);
  const [nonconformityIds, setNonconformityIds] = useState<string[]>(
    search.nonconformityId ? [search.nonconformityId] : [],
  );
  const [ncSearch, setNcSearch] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Só NCs em tratamento entram numa SACAP: uma NC ainda não avaliada não tem
   * causa a analisar, e uma encerrada não tem o que tratar.
   */
  const ncFilter = useMemo(
    () => ({
      page: 1,
      pageSize: 50,
      status: TREATABLE_STATUSES,
      ...(ncSearch ? { search: ncSearch } : {}),
    }),
    [ncSearch],
  );

  /*
   * O catálogo de tipos, só os ATIVOS.
   *
   * Deixou de ser um enum no código: a organização administra os próprios
   * tipos, e é a linha escolhida que diz se esta reunião vai exigir vínculo com
   * não-conformidade e avaliação de eficácia. Nada aqui compara com "SACAP".
   */
  const typesQuery = useQuery({
    queryKey: queryKeys.meetingTypes.all(),
    queryFn: () => meetingTypesApi.list(),
  });

  const meetingTypes: MeetingType[] = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);
  const selectedType = useMemo(
    () => meetingTypes.find((type) => type.id === meetingTypeId) ?? null,
    [meetingTypes, meetingTypeId],
  );

  /*
   * Um tipo só: escolhe sozinho. Vários: exige a escolha.
   *
   * Pré-selecionar o primeiro de uma lista longa foi o que fez "reunião" e
   * "SACAP" virarem sinônimos — o formulário nascia com SACAP marcado e ninguém
   * reparava que era uma escolha.
   */
  useEffect(() => {
    if (meetingTypeId === '' && meetingTypes.length === 1) {
      setMeetingTypeId(meetingTypes[0]!.id);
    }
  }, [meetingTypes, meetingTypeId]);

  const ncQuery = useQuery({
    queryKey: queryKeys.nonconformities.list(ncFilter),
    queryFn: () => nonconformitiesApi.list(ncFilter),
    placeholderData: keepPreviousData,
  });

  const ncOptions = useMemo(
    () =>
      (ncQuery.data?.items ?? []).map((item) => ({
        value: item.id,
        label: `${item.code} — ${item.title}`,
        hint: item.statusLabel,
      })),
    [ncQuery.data],
  );

  const selectedNcs = useMemo(
    () =>
      nonconformityIds.map((id) => ({
        id,
        label: ncOptions.find((option) => option.value === id)?.label ?? id,
      })),
    [nonconformityIds, ncOptions],
  );

  const requiresNc = selectedType?.requiresNonconformity ?? false;
  const invalidWindow = Boolean(startsAt && endsAt && new Date(endsAt) <= new Date(startsAt));

  /*
   * VALIDAÇÃO NO ENVIO, e não botão desabilitado.
   *
   * O formulário só desabilitava o botão: quem esquecia um campo ficava
   * olhando para um botão morto sem saber o que faltava. Agora cada campo tem
   * a sua mensagem, o foco vai para o primeiro problema e o resumo é anunciado.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (subject.trim() === '') errors.subject = 'Informe o assunto da reunião.';
    if (meetingTypeId === '') errors.meetingTypeId = 'Escolha o tipo da reunião.';
    if (location.trim() === '') errors.location = 'Informe o local da reunião.';
    if (startsAt === '') errors.startsAt = 'Informe a data e hora de início.';
    if (endsAt === '') errors.endsAt = 'Informe a data e hora de término.';
    if (invalidWindow) errors.endsAt = 'O término precisa ser posterior ao início.';
    if (agenda.trim() === '') errors.agenda = 'Informe a pauta da reunião.';
    if (participantUserIds.length === 0) {
      errors.participantUserIds = 'A reunião precisa de ao menos um participante.';
    }
    if (requiresNc && nonconformityIds.length === 0) {
      errors.nonconformityIds = selectedType
        ? `O tipo "${selectedType.name}" exige o vínculo com ao menos uma não-conformidade.`
        : 'Este tipo exige vínculo com não-conformidade.';
    }
    return errors;
  }

  const submit = useMutation({
    mutationFn: (): Promise<{ id: string }> => {
      const input: CreateMeetingInput = {
        subject,
        meetingTypeId,
        location,
        startsAt: new Date(startsAt),
        endsAt: new Date(endsAt),
        agenda,
        participantUserIds,
        nonconformityIds,
      };
      return meetingsApi.create(input);
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.meetings.all() });
      toast({
        tone: 'success',
        title: 'Reunião agendada',
        description:
          'Os participantes foram convidados. A confirmação de presença de cada um fica registrada e a reunião só muda de situação quando alguém a abrir.',
      });
      void navigate({ to: '/reunioes/$meetingId', params: { meetingId: created.id } });
    },
    onError: (error) => setSubmitError(toUserMessage(error)),
  });


  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Reuniões', to: '/reunioes' }, { label: 'Nova' }]}
        title="Agendar reunião"
        description="Pauta, participantes e não-conformidades tratadas. A SACAP é o contêiner do tratamento: é nela que a análise de causa acontece."
      />

      <PageBody>
        <form
          noValidate
          className="flex max-w-4xl flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitError(null);
            const errors = validate();
            setFieldErrors(errors);
            if (Object.keys(errors).length > 0) {
              // O foco vai para o primeiro campo com problema: rolar a tela
              // atrás da mensagem é trabalho do formulário, não do usuário.
              const primeiro = document.querySelector<HTMLElement>('[aria-invalid="true"]');
              primeiro?.focus();
              return;
            }
            submit.mutate();
          }}
        >
          {submitError ? (
            <FormError title="Não foi possível agendar a reunião">{submitError}</FormError>
          ) : null}

          <Card className="flex flex-col gap-4">
            <CardHeader headingLevel={2} title="Dados da reunião" />

            <Field label="Assunto" required>
              <Input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={255}
                autoFocus
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Tipo"
                required
                error={fieldErrors.meetingTypeId}
                description={
                  selectedType
                    ? [
                        selectedType.requiresNonconformity
                          ? 'Exige vínculo com não-conformidade.'
                          : 'Não exige vínculo com não-conformidade.',
                        selectedType.requiresEffectiveness
                          ? 'Pedirá avaliação de eficácia depois do encerramento.'
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' ')
                    : 'O tipo define o que a reunião vai exigir.'
                }
              >
                {typesQuery.isPending ? (
                  <Input value="Carregando tipos…" readOnly disabled />
                ) : meetingTypes.length === 0 ? (
                  <EmptyState
                    title="Nenhum tipo de reunião ativo"
                    description="Sem tipo não há como agendar. Cadastre ou reative um tipo em Tipos de reunião."
                    action={
                      <Button asChild variant="secondary" size="sm">
                        <Link to="/tipos-reuniao">Abrir Tipos de reunião</Link>
                      </Button>
                    }
                  />
                ) : (
                  <Select
                    value={meetingTypeId}
                    onValueChange={setMeetingTypeId}
                    placeholder="Escolha o tipo"
                    criar={{
                      rotulo: 'Novo tipo de reunião',
                      dialogo: (props) => <CadastroRapidoDeTipoDeReuniao {...props} />,
                    }}
                    options={meetingTypes.map((type) => ({
                      value: type.id,
                      label: type.name,
                    }))}
                  />
                )}
              </Field>

              <Field label="Local" required>
                <Input
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                  maxLength={255}
                  placeholder="Sala, endereço ou link da videoconferência"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Início" required>
                <Input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(event) => setStartsAt(event.target.value)}
                />
              </Field>

              <Field
                label="Término"
                required
                error={invalidWindow ? 'O término precisa ser posterior ao início.' : undefined}
              >
                <Input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(event) => setEndsAt(event.target.value)}
                />
              </Field>
            </div>

            <Field
              label="Pauta"
              required
              description="O que será tratado. A pauta entra na ata e é o que dá contexto a quem lê o registro depois."
            >
              <Textarea
                value={agenda}
                onChange={(event) => setAgenda(event.target.value)}
                rows={6}
                maxLength={20_000}
                showCounter
              />
            </Field>
          </Card>

          <Card className="flex flex-col gap-4">
            <CardHeader
              headingLevel={2}
              title="Participantes"
              description="Cada participante recebe o convite e registra a própria confirmação de presença. A presença efetiva é apurada por quem conduz, no encerramento."
            />
            <PeoplePicker
              noun="participantes"
              userFieldLabel="Adicionar participante"
              userFieldDescription="Uma reunião precisa de ao menos um participante — sem participantes não há ata nem evidência de análise crítica."
              users={directory.subjects.map((subject_) => ({
                id: subject_.userId,
                name: subject_.displayName,
                hint: subject_.jobTitle,
              }))}
              userIds={participantUserIds}
              onUserIdsChange={setParticipantUserIds}
              emptyHint="Escolha quem participa da reunião. Sem participante, o encerramento é recusado pelo servidor."
            />
          </Card>

          <Card className="flex flex-col gap-4">
            <CardHeader
              headingLevel={2}
              title="Não-conformidades tratadas"
              description="Somente registros procedentes e ainda em tratamento aparecem na busca: uma NC não avaliada não tem causa a analisar."
            />

            <Field
              label="Vincular não-conformidade"
              required={requiresNc}
              error={fieldErrors.nonconformityIds}
              description={
                requiresNc
                  ? 'O tipo escolhido exige o vínculo.'
                  : 'Opcional para este tipo. Vincular uma NC a uma reunião de rotina é permitido.'
              }
            >
              <Combobox
                items={ncOptions.filter((option) => !nonconformityIds.includes(option.value))}
                value={null}
                loading={ncQuery.isPending}
                onSearchChange={setNcSearch}
                onValueChange={(id) => {
                  if (id) setNonconformityIds((current) => [...current, id]);
                }}
                placeholder="Buscar por número ou título…"
                emptyMessage="Nenhuma não-conformidade em tratamento encontrada."
              />
            </Field>

            {selectedNcs.length > 0 ? (
              <ul
                aria-label="Não-conformidades vinculadas à reunião"
                className="flex flex-wrap gap-1.5"
              >
                {selectedNcs.map((item) => (
                  <li key={item.id}>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken py-0.5 pl-2.5 pr-1 text-sm text-content">
                      <Badge tone="outline" size="sm">
                        NC
                      </Badge>
                      <span className="min-w-0 truncate">{item.label}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        iconLabel={`Desvincular ${item.label} da reunião`}
                        className="h-5 w-5"
                        onClick={() =>
                          setNonconformityIds((current) =>
                            current.filter((id) => id !== item.id),
                          )
                        }
                      >
                        <IconClose size={12} />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            {requiresNc && nonconformityIds.length === 0 ? (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
              >
                <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  <strong className="font-semibold">SACAP sem não-conformidade não fecha.</strong>{' '}
                  O encerramento é recusado pelo servidor enquanto não houver vínculo. Escolha ao
                  menos um registro acima ou mude o tipo da reunião.
                </span>
              </p>
            ) : null}
          </Card>

          <ButtonGroup>
            <Button
              variant="secondary"
              disabled={submit.isPending}
              onClick={() => void navigate({ to: '/reunioes' })}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submit.isPending}
            >
              Agendar reunião
            </Button>
          </ButtonGroup>
        </form>
      </PageBody>
    </>
  );
}
